import { ReactElement, useState, useRef, useEffect } from "react";
import { Box } from "@twilio-paste/core/box";
import { Anchor } from "@twilio-paste/core/anchor";
import { DetailText } from "@twilio-paste/core/detail-text";
import { ChevronDownIcon } from "@twilio-paste/icons/esm/ChevronDownIcon";
import { ChevronUpIcon } from "@twilio-paste/icons/esm/ChevronUpIcon";
import { Truncate } from "@twilio-paste/core/truncate";
import { Button } from "@twilio-paste/core/button";
import { styled } from "@twilio-paste/core/styling-library";
import {
  LinkGroupBlock,
  trackingDetails,
  WiseOwlElementIds,
} from "@twiliointernal/e2-commons-shared-definitions";
import { useResizeObserver } from "@twiliointernal/e2-commons-shared-hooks";
import { constants, copy } from "../definitions";
import { useWiseOwlContext } from "../../../hooks";

const LinkAnchorContainer = styled(Box)`
  [data-paste-element="ANCHOR"] > span {
    display: flex;
  }
`;

interface LinkGroupProps {
  block: LinkGroupBlock;
}

const LinkGroup = ({ block }: LinkGroupProps): ReactElement => {
  const ref = useRef<HTMLElement>(null);
  const {
    chatId,
    handlers: { handleTrackEvent },
    applicationProps: { displayName, ingress },
  } = useWiseOwlContext();
  const {
    values: { links },
  } = block;

  const [isExpanded, setIsExpanded] = useState(false);
  const [linkCountToRender, setLinkCountToRender] = useState(
    constants.MAX_LINKS_TO_RENDER
  );

  const { width } = useResizeObserver(ref);

  useEffect(() => {
    if (width < constants.SM_BREAKPOINT_PX) {
      setLinkCountToRender(constants.MIN_LINKS_TO_RENDER);
    } else {
      setLinkCountToRender(constants.MAX_LINKS_TO_RENDER);
    }
  }, [width]);

  const handlePreviewDescriptionTruncation = (previewText: string): string => {
    const strippedPreviewTest = previewText
      .substring(0, constants.LINK_PREVIEW_CHARACTER_LIMIT)
      .trim();
    const truncatedPreview = `${strippedPreviewTest}...`;

    return truncatedPreview;
  };

  const hasViewMore = links.length > linkCountToRender;
  const linksToRender = isExpanded ? links : links.slice(0, linkCountToRender);

  const handleLinkTrackingEvent = (
    linkTitle: string,
    linkUrl: string
  ): void => {
    if (handleTrackEvent) {
      handleTrackEvent(trackingDetails.wiseOwl.linkClicked(displayName), {
        chatId,
        ingress,
        title: linkTitle,
        url: linkUrl,
      });
    }
  };

  return (
    <Box
      as="div"
      element={WiseOwlElementIds.WISE_OWL_LINK_GROUP_BLOCK}
      display="flex"
      flexDirection="column"
      rowGap="space20"
      maxWidth="766px"
      ref={ref}
    >
      <Box
        as="section"
        alignItems="center"
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr));"
        columnGap="space30"
        rowGap="space30"
      >
        {linksToRender.map((link, index) => (
          <Box
            key={`link-${link.title}-${index}`}
            as="article"
            borderWidth="borderWidth10"
            borderColor={["colorBorderWeaker", "colorBorder"]}
            borderStyle="solid"
            borderRadius="borderRadius30"
            padding="space40"
          >
            <LinkAnchorContainer maxWidth="15rem">
              <Anchor href={link.url} showExternal>
                <Truncate
                  onClick={() => handleLinkTrackingEvent(link.title, link.url)}
                  title={link.title}
                >
                  {link.title}
                </Truncate>
              </Anchor>
            </LinkAnchorContainer>

            <DetailText>
              {handlePreviewDescriptionTruncation(link.preview)}
            </DetailText>
          </Box>
        ))}
      </Box>

      {hasViewMore && (
        <Box display="flex" alignSelf="center">
          <Button
            variant="link"
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? copy.linkGroup.viewLess : copy.linkGroup.viewMore}
            {isExpanded ? (
              <ChevronUpIcon decorative size="sizeIcon50" />
            ) : (
              <ChevronDownIcon decorative size="sizeIcon50" />
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default LinkGroup;
